import {
  formatNumberDisplay,
  formatNumberInput,
  parseNumberInput,
} from '../utils/numberFormat';

export type PurchaseOrderStatus = 'DRAFT' | 'ORDERED' | 'CLOSED' | 'CANCELLED' | 'APPROVED';
export type PurchaseOrderAttachmentKind = 'DRAWING' | 'SPEC' | 'OTHER';

export interface PurchaseOrderItem {
  itemId?: number;
  inventoryId: number;
  itemName?: string | null;
  specText?: string | null;
  unit?: string | null;
  orderQty: number | string;
  unitPrice?: number | string | null;
  /** orderQty * unitPrice */
  amount?: number | string | null;
  remark?: string | null;
  sortOrder?: number | null;
}

export interface PurchaseOrderAttachment {
  attachmentId: number;
  attachmentKind?: PurchaseOrderAttachmentKind | string | null;
  fileName: string;
  contentType?: string | null;
  fileSize?: number | null;
  createdAt?: string | null;
}

export interface PurchaseOrder {
  purchaseOrderId: number;
  contractId?: number | null;
  contractName?: string | null;
  contractNo?: string | null;
  orderNo?: string | null;
  orderDate?: string | null;
  deliveryDueDate?: string | null;
  vendorId?: number | null;
  vendorName?: string | null;
  vendorContactName?: string | null;
  vendorPhone?: string | null;
  vendorFax?: string | null;
  status: PurchaseOrderStatus | string;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  canCancelOrdered?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  memo?: string | null;
  itemCount?: number | null;
  attachmentCount?: number | null;
  totalAmount?: number | string | null;
  items?: PurchaseOrderItem[] | null;
  attachments?: PurchaseOrderAttachment[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PurchaseOrderItemPayload {
  inventoryId: number;
  itemName?: string;
  specText?: string;
  unit?: string;
  orderQty: number;
  unitPrice?: number | null;
  remark?: string;
  sortOrder?: number;
}

export interface PurchaseOrderPayload {
  contractId?: number | null;
  orderNo?: string;
  orderDate?: string | null;
  deliveryDueDate?: string | null;
  vendorId?: number | null;
  vendorName?: string;
  vendorContactName?: string;
  vendorPhone?: string;
  vendorFax?: string;
  status?: PurchaseOrderStatus | string;
  memo?: string;
  items: PurchaseOrderItemPayload[];
}

export function purchaseOrderStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
  hasApprover?: boolean | null
) {
  if (approvalInProgress) return '결재 요청 중';
  switch (status) {
    case 'DRAFT':
      return '작성완료';
    case 'ORDERED':
      return hasApprover === false ? '발주완료 (미승인)' : '발주완료';
    case 'CLOSED':
      return '마감';
    case 'CANCELLED':
      return '취소';
    case 'APPROVED':
      return '승인완료';
    default:
      return status || '-';
  }
}

/** 승인 스탬프(승인자)가 있는지 — 발주완료 후 승인 루트 판별용 */
export function purchaseOrderHasApprover(detail?: {
  approverUserId?: number | null;
  approverName?: string | null;
} | null) {
  if (!detail) return false;
  if (detail.approverUserId != null) return true;
  return Boolean(detail.approverName?.trim());
}

export function formatOrderQty(value?: number | string | null) {
  if (value == null || value === '') return '-';
  const n = parseNumberInput(value);
  if (n == null) return String(value);
  return n.toLocaleString('ko-KR');
}

export function calcOrderLineAmount(
  qty?: number | string | null,
  unitPrice?: number | string | null
) {
  const q = parseNumberInput(qty == null ? null : String(qty));
  const p = parseUnitPriceInput(unitPrice == null ? null : String(unitPrice));
  if (q == null || p == null) return null;
  return q * p;
}

/** @deprecated use parseNumberInput — 단가/금액 호환용 */
export function parseUnitPriceInput(raw?: string | null): number | null {
  return parseNumberInput(raw);
}

/** @deprecated use formatNumberInput */
export function formatUnitPriceInput(raw: string): string {
  return formatNumberInput(raw);
}

/** @deprecated use formatNumberDisplay */
export function formatUnitPriceDisplay(value?: number | string | null): string {
  return formatNumberDisplay(value);
}

export function formatWon(value?: number | string | null) {
  if (value == null || value === '') return '-';
  const n = parseNumberInput(value);
  if (n == null) return String(value);
  return `${n.toLocaleString('ko-KR')}원`;
}

export function purchaseOrderAttachmentKindLabel(kind?: string | null) {
  switch (kind) {
    case 'DRAWING':
      return '도면';
    case 'SPEC':
      return '사양서';
    case 'OTHER':
      return '기타';
    default:
      return kind || '기타';
  }
}

export function formatPurchaseOrderFileSize(size?: number | null) {
  if (size == null || size < 0) return '';
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}
